import type { LegacyAccessFlag, Permission, Role } from "./types";

export type RoleDefinition = {
  key: Role;
  label: string;
  description: string;
  permissions: Permission[];
  legacyFlags: LegacyAccessFlag[];
};

const adminPermissions: Permission[] = [
  // System
  "admin.access", "dashboard.view", "reports.view", "settings.view", "settings.edit",
  "users.view", "users.manage", "users.create", "users.edit", "users.disable", "users.delete",
  "roles.manage", "identity.property.manage", "identity.property.assign",
  // PMS — full
  "pms.access", "pms.dashboard.view",
  "pms.incidents.update",
  "pms.reservations.view", "pms.reservations.create", "pms.reservations.edit",
  "pms.reservations.cancel", "pms.reservations.checkin", "pms.reservations.checkout", "pms.reservations.noshow",
  "pms.groups.view",
  "pms.rooms.view", "pms.rooms.assign", "pms.rooms.block",
  "pms.housekeeping.view", "pms.housekeeping.update", "pms.housekeeping.assign",
  "pms.guests.view", "pms.guests.create", "pms.guests.edit", "pms.guests.merge",
  "pms.billing.view", "pms.billing.post", "pms.billing.payment",
  "pms.billing.discount", "pms.billing.void", "pms.billing.comp", "pms.billing.transfer", "pms.billing.close",
  "pms.rates.view", "pms.rates.override",
  "pms.nightaudit.run", "pms.nightaudit.view",
  "pms.reports.view", "pms.reports.financial",
  // POS — full
  "pos.access",
  "pos.sales.view", "pos.sales.create", "pos.sales.edit", "pos.sales.void", "pos.sales.discount", "pos.sales.comp",
  "pos.cashier.open", "pos.cashier.payment", "pos.cashier.refund", "pos.cashier.approve_shift",
  "pos.menu.view", "pos.menu.edit",
  "pos.tables.view", "pos.tables.assign",
  "pos.reports.view", "pos.reports.financial",
  // EMS — full
  "ems.access", "ems.assets.view", "ems.assets.edit",
  "ems.maintenance.view", "ems.maintenance.create", "ems.maintenance.edit", "ems.reports.view",
  // WMS — full
  "wms.access", "wms.inventory.view", "wms.inventory.adjust",
  "wms.orders.view", "wms.orders.create", "wms.orders.approve", "wms.orders.receive",
  "wms.suppliers.view", "wms.suppliers.edit",
  // Revenue — full
  "revenue.access", "revenue.analytics.view", "revenue.analytics.export", "revenue.pricing.view", "revenue.pricing.edit",
  // VoIP — full
  "voip.access", "voip.calls.view", "voip.calls.manage", "voip.config.view", "voip.config.edit",
  // Guests — full
  "guests.access", "guests.profiles.view", "guests.profiles.edit",
  "guests.communication.view", "guests.communication.send",
  "guests.loyalty.view", "guests.loyalty.edit",
  // CCTV — full
  "cctv.access", "cctv.live.view",
  "cctv.cameras.view", "cctv.cameras.manage",
  "cctv.recordings.view", "cctv.recordings.export", "cctv.recordings.delete",
  "cctv.events.view", "cctv.events.acknowledge",
  "cctv.incidents.view", "cctv.incidents.manage",
  "cctv.access_logs.view",
  "cctv.privacy.view", "cctv.privacy.manage",
  "cctv.health.view", "cctv.audit.view",
  // Keycoder — full
  "keycoder.access", "keycoder.cards.issue", "keycoder.cards.cancel", "keycoder.devices.view", "keycoder.devices.manage",
  // Garage — full
  "garage.access", "garage.parking.view", "garage.parking.manage", "garage.settings.view", "garage.settings.edit",
];

export const ROLE_DEFINITIONS: Record<Role, RoleDefinition> = {
  admin: {
    key: "admin",
    label: "Administrator",
    description: "Full system access across all Xentros HOS modules.",
    permissions: adminPermissions,
    legacyFlags: ["GLOBAL_ADMIN", "admin"],
  },

  general_manager: {
    key: "general_manager",
    label: "General Manager",
    description: "Operational oversight across all departments with reporting access.",
    permissions: [
      "dashboard.view", "reports.view", "settings.view",
      "users.view", "users.create", "users.edit", "users.disable",
      "roles.manage", "identity.property.manage", "identity.property.assign",
      // PMS — full operational
      "pms.access", "pms.dashboard.view",
      "pms.incidents.update",
      "pms.reservations.view", "pms.reservations.create", "pms.reservations.edit",
      "pms.reservations.cancel", "pms.reservations.checkin", "pms.reservations.checkout", "pms.reservations.noshow",
      "pms.groups.view",
      "pms.rooms.view", "pms.rooms.assign", "pms.rooms.block",
      "pms.guests.view", "pms.guests.create", "pms.guests.edit", "pms.guests.merge",
      "pms.billing.view", "pms.billing.post", "pms.billing.payment",
      "pms.billing.discount", "pms.billing.void", "pms.billing.comp", "pms.billing.transfer", "pms.billing.close",
      "pms.rates.view", "pms.rates.override",
      "pms.housekeeping.view", "pms.housekeeping.assign",
      "pms.nightaudit.view", "pms.reports.view", "pms.reports.financial",
      // POS
      "pos.access",
      "pos.sales.view", "pos.sales.create", "pos.sales.edit", "pos.sales.void", "pos.sales.discount", "pos.sales.comp",
      "pos.cashier.open", "pos.cashier.payment", "pos.cashier.refund", "pos.cashier.approve_shift",
      "pos.menu.view", "pos.menu.edit", "pos.tables.view", "pos.tables.assign",
      "pos.reports.view", "pos.reports.financial",
      // Revenue
      "revenue.access", "revenue.analytics.view", "revenue.analytics.export", "revenue.pricing.view",
      // Guests
      "guests.access", "guests.profiles.view", "guests.profiles.edit",
      "guests.communication.view", "guests.communication.send",
      "guests.loyalty.view", "guests.loyalty.edit",
    ],
    legacyFlags: ["admin"],
  },

  manager: {
    key: "manager",
    label: "Manager",
    description: "Department-level management with operational and reporting access.",
    permissions: [
      "dashboard.view", "reports.view",
      "users.view",
      // PMS
      "pms.access", "pms.dashboard.view",
      "pms.incidents.update",
      "pms.reservations.view", "pms.reservations.create", "pms.reservations.edit",
      "pms.reservations.cancel", "pms.reservations.checkin", "pms.reservations.checkout",
      "pms.groups.view",
      "pms.rooms.view", "pms.rooms.assign",
      "pms.guests.view", "pms.guests.create", "pms.guests.edit",
      "pms.billing.view", "pms.billing.post", "pms.billing.payment", "pms.billing.discount",
      "pms.housekeeping.view", "pms.housekeeping.assign",
      "pms.rates.view", "pms.nightaudit.view", "pms.reports.view",
      // Revenue
      "revenue.access", "revenue.analytics.view",
      // Guests
      "guests.access", "guests.profiles.view", "guests.profiles.edit",
    ],
    legacyFlags: ["admin"],
  },

  supervisor: {
    key: "supervisor",
    label: "Supervisor",
    description: "Team-level oversight with PMS and basic reporting access.",
    permissions: [
      "dashboard.view", "reports.view", "users.view",
      // PMS
      "pms.access", "pms.dashboard.view",
      "pms.incidents.update",
      "pms.reservations.view", "pms.reservations.edit",
      "pms.reservations.checkin", "pms.reservations.checkout",
      "pms.groups.view",
      "pms.rooms.view", "pms.rooms.assign",
      "pms.guests.view",
      "pms.billing.view", "pms.billing.discount",
      "pms.housekeeping.view", "pms.housekeeping.assign",
      "pms.reports.view",
    ],
    legacyFlags: [],
  },

  front_office: {
    key: "front_office",
    label: "Front Office",
    description: "Guest-facing reservations, check-in/out and folio management.",
    permissions: [
      "dashboard.view",
      "pms.access", "pms.dashboard.view",
      "pms.incidents.update",
      "pms.reservations.view", "pms.reservations.create", "pms.reservations.edit",
      "pms.reservations.cancel", "pms.reservations.checkin", "pms.reservations.checkout", "pms.reservations.noshow",
      "pms.groups.view",
      "pms.rooms.view", "pms.rooms.assign",
      "pms.housekeeping.assign",
      "pms.guests.view", "pms.guests.create", "pms.guests.edit",
      "pms.billing.view", "pms.billing.post", "pms.billing.payment",
    ],
    legacyFlags: [],
  },

  housekeeping: {
    key: "housekeeping",
    label: "Housekeeping",
    description: "Room status board and servicing task management.",
    permissions: [
      "dashboard.view",
      "pms.access",
      "pms.housekeeping.view", "pms.housekeeping.update", "pms.housekeeping.assign",
      "pms.rooms.view",
    ],
    legacyFlags: [],
  },

  finance: {
    key: "finance",
    label: "Finance",
    description: "Full billing, folio, financial reporting and revenue access.",
    permissions: [
      "dashboard.view", "reports.view",
      "pms.access",
      "pms.reservations.view",
      "pms.billing.view", "pms.billing.post", "pms.billing.payment",
      "pms.billing.discount", "pms.billing.void", "pms.billing.comp", "pms.billing.transfer", "pms.billing.close",
      "pms.nightaudit.view", "pms.reports.view", "pms.reports.financial",
      "revenue.access", "revenue.analytics.view", "revenue.analytics.export",
    ],
    legacyFlags: [],
  },

  engineering: {
    key: "engineering",
    label: "Engineering",
    description: "Technical systems, asset and maintenance management.",
    permissions: [
      "dashboard.view",
      "pms.rooms.view",
      "ems.access", "ems.assets.view", "ems.assets.edit",
      "ems.maintenance.view", "ems.maintenance.create", "ems.maintenance.edit", "ems.reports.view",
    ],
    legacyFlags: [],
  },

  staff: {
    key: "staff",
    label: "Staff",
    description: "Basic operational access for day-to-day front-line tasks.",
    permissions: [
      "dashboard.view",
      "pms.access", "pms.reservations.view", "pms.guests.view", "pms.rooms.view",
      "pos.access", "pos.sales.view", "pos.sales.create", "pos.cashier.payment", "pos.tables.view",
    ],
    legacyFlags: [],
  },

  // Compatibility roles — minimal permissions, should not be actively assigned
  user: {
    key: "user",
    label: "User",
    description: "Compatibility role for legacy accounts.",
    permissions: ["dashboard.view", "pms.access", "pms.reservations.view"],
    legacyFlags: [],
  },
  guest: {
    key: "guest",
    label: "Guest User",
    description: "Compatibility role with no operational access.",
    permissions: [],
    legacyFlags: [],
  },
};

export const ROLE_KEYS = Object.keys(ROLE_DEFINITIONS) as Role[];
